import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { PALETTE as P, CARD, NUM, TYPE, SPACE } from "./tokens";
import InfoHint from "../../components/InfoHint";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

// Counts from the PREVIOUS value to the new one, so a range change reads as the
// number moving rather than a different number appearing. 1.2s on first paint,
// 600ms afterwards — the entrance plays once and is never replayed.
//
// Returns the final value immediately for anyone who asked for less motion, and
// for a value that is not a number ("—", a percentage string) it passes through
// untouched rather than trying to tween it.
export const useCountUp = (value, { duration = 1200, delay = 250 } = {}) => {
  const numeric = typeof value === "number" && Number.isFinite(value);
  const [shown, setShown] = useState(numeric ? 0 : value);
  const from = useRef(0);
  const first = useRef(true);
  const raf = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!numeric) {
      setShown(value);
      return undefined;
    }

    let reduced = false;
    try {
      reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      reduced = false;
    }
    if (reduced) {
      setShown(value);
      from.current = value;
      first.current = false;
      return undefined;
    }

    const start = from.current;
    const span = value - start;
    const ms = first.current ? duration : 600;
    const wait = first.current ? delay : 0;

    const run = () => {
      let t0 = null;
      const step = (ts) => {
        if (t0 === null) t0 = ts;
        const p = Math.min(1, (ts - t0) / ms);
        setShown(Math.round(start + span * easeOutCubic(p)));
        if (p < 1) raf.current = requestAnimationFrame(step);
        else {
          from.current = value;
          first.current = false;
        }
      };
      raf.current = requestAnimationFrame(step);
    };

    timer.current = setTimeout(run, wait);
    return () => {
      clearTimeout(timer.current);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [value, numeric, duration, delay]);

  return shown;
};

export const CountUp = ({ value, sx }) => {
  const shown = useCountUp(value);
  return (
    <Typography component="span" sx={{ ...NUM, ...sx }}>
      {shown === null || shown === undefined ? "—" : shown}
    </Typography>
  );
};

export const Delta = ({ value, higherIsBetter = true }) => {
  if (value === null || value === undefined) return null;
  const rising = value > 0;
  const good = rising === Boolean(higherIsBetter);
  const tone = value === 0 ? P.flat : good ? P.up : P.down;
  return (
    <Box
      component="span"
      className="dv-item"
      style={{ animationDelay: ".9s" }}
      sx={{
        ...NUM,
        fontSize: TYPE.micro,
        fontWeight: 600,
        padding: "2px 7px",
        borderRadius: "6px",
        background: tone.bg,
        color: tone.fg,
        whiteSpace: "nowrap",
      }}
    >
      {value === 0 ? "0%" : `${rising ? "↑" : "↓"} ${Math.abs(value)}%`}
    </Box>
  );
};

export const Card = ({ title, subtitle, hint, right, delay = 0, children, sx, className = "dv-card" }) => (
  <Box
    className={className}
    style={{ animationDelay: `${delay}s` }}
    sx={{ ...CARD, display: "flex", flexDirection: "column", gap: SPACE.lg, ...sx }}
  >
    {(title || right) && (
      <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="flex-start" gap={SPACE.sm}>
        <Box sx={{ minWidth: 0 }}>
          <Box display="flex" alignItems="center">
            <Typography component="h2" sx={{ margin: 0, fontSize: TYPE.section, fontWeight: 600, color: P.ink }}>
              {title}
            </Typography>
            {hint && <InfoHint text={hint} label={title} />}
          </Box>
          {subtitle && (
            <Typography sx={{ margin: "2px 0 0", fontSize: TYPE.meta, color: P.inkMuted }}>{subtitle}</Typography>
          )}
        </Box>
        {right}
      </Box>
    )}
    {children}
  </Box>
);

export const Legend = ({ keys }) => (
  <Box display="flex" flexWrap="wrap" gap={SPACE.md}>
    {keys.map((k) => (
      <Box key={k.label} display="flex" alignItems="center" gap={0.75}>
        <Box sx={{ width: 9, height: 9, borderRadius: "3px", background: k.color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{k.label}</Typography>
      </Box>
    ))}
  </Box>
);

// A bar whose FILL is scaled, never widened. The element is laid out at its
// final width and the inner span scales from 0, so the browser never reflows
// mid-animation.
export const Fill = ({ ratio, color, delay = 0, height = 8, hatched = false }) => (
  <Box sx={{ height, borderRadius: "99px", background: P.track, overflow: "hidden" }}>
    <Box
      className={`dv-fill${hatched ? " dv-hatch" : ""}`}
      style={{ animationDelay: `${delay}s`, transform: `scaleX(${Math.max(ratio, 0).toFixed(3)})` }}
      sx={{ display: "block", height: "100%", borderRadius: "99px", background: hatched ? undefined : color }}
    />
  </Box>
);

// Ranked rows. "Unknown" is a residual bucket, so it is hatched and italic —
// an admin can see that the longest bar is absent data, not a finding.
//
// A row that carries sectors opens to them in place. The whole header is the
// control, not just the chevron: the footer tells an admin to "select a
// category", and a 24px target beside a full-width row is not what that
// sentence promises.
export const RankedBarList = ({ rows, footer, delay = 0, max: maxOverride, expandable = false }) => {
  const [open, setOpen] = useState({});
  const max = maxOverride ?? (rows ?? []).reduce((m, r) => Math.max(m, r.count), 0);
  const toggle = (label) => setOpen((s) => ({ ...s, [label]: !s[label] }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: SPACE.sm }}>
      {(rows ?? []).map((r, i) => {
        const unknown = r.label === "Unknown";
        const sectors = r.sectors ?? [];
        // Only a row that HAS sectors gets a control. A chevron on a flat row
        // promises a drawer that does not exist.
        const opens = expandable && sectors.length > 0;
        const isOpen = Boolean(open[r.label]);

        const head = (
          <>
            <Typography
              component="span"
              sx={{
                fontSize: TYPE.label,
                color: unknown ? P.inkMuted : P.ink,
                fontStyle: unknown ? "italic" : "normal",
              }}
            >
              {r.label}
            </Typography>
            <Box component="span" display="inline-flex" alignItems="center" gap={0.75}>
              {opens && (
                <Box
                  component="span"
                  sx={{
                    width: 24,
                    height: 24,
                    color: P.inkMuted,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "6px",
                    // Rotation is the state, so the chevron never disagrees
                    // with what is on screen below it.
                    transform: isOpen ? "rotate(180deg)" : "none",
                    transition: "transform 160ms ease",
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </Box>
              )}
              <Typography component="span" sx={{ ...NUM, fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>
                {r.count}
              </Typography>
            </Box>
          </>
        );

        return (
          <Box key={r.label} className="dv-item" style={{ animationDelay: `${delay + i * 0.04}s` }}>
            <Box
              {...(opens
                ? {
                    component: "button",
                    type: "button",
                    onClick: () => toggle(r.label),
                    "aria-expanded": isOpen,
                    // Named explicitly: the row's own text would read as
                    // "Technology 1", which says nothing about what it does.
                    "aria-label": `${isOpen ? "Hide" : "Show"} sectors for ${r.label}`,
                  }
                : {})}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={SPACE.sm}
              sx={{
                marginBottom: "5px",
                ...(opens
                  ? {
                      width: "100%",
                      padding: 0,
                      border: 0,
                      font: "inherit",
                      textAlign: "left",
                      background: "transparent",
                      cursor: "pointer",
                      borderRadius: "6px",
                      "&:focus-visible": { outline: `2px solid ${P.accent}`, outlineOffset: "2px" },
                    }
                  : {}),
              }}
            >
              {head}
            </Box>
            <Fill ratio={max ? r.count / max : 0} color={P.accent} delay={delay + i * 0.06} hatched={unknown} />

            {/* Scaled against the PARENT's own count, not the list max: a
                drawer is a subdivision, so its bars read as fractions of the
                row above and can never overrun it. Against the list max a
                sector of 1 under a list topped by 26 came out at 4% of a 5px
                bar — technically drawn, visually an empty track.

                Mounted only while open — a closed drawer should not be
                reachable by keyboard or by find-in-page. */}
            {opens && isOpen && (
              <Box sx={{ paddingLeft: SPACE.md, marginTop: "6px", display: "flex", flexDirection: "column", gap: SPACE.xs }}>
                {sectors.map((sec, j) => {
                  const secUnknown = sec.label === "Unknown";
                  return (
                    <Box key={sec.label} className="dv-item" style={{ animationDelay: `${j * 0.03}s` }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={SPACE.sm} sx={{ marginBottom: "4px" }}>
                        <Typography
                          sx={{
                            fontSize: TYPE.micro,
                            color: P.inkMuted,
                            fontStyle: secUnknown ? "italic" : "normal",
                          }}
                        >
                          {sec.label}
                        </Typography>
                        <Typography sx={{ ...NUM, fontSize: TYPE.micro, color: P.inkSoft }}>{sec.count}</Typography>
                      </Box>
                      <Fill
                        ratio={r.count ? sec.count / r.count : 0}
                        color={P.accentNested}
                        delay={0}
                        height={5}
                        hatched={secUnknown}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
      {(rows ?? []).length === 0 && (
        <Typography sx={{ fontSize: TYPE.label, color: P.inkMuted }}>Nothing in this range.</Typography>
      )}
      {footer && (
        <Typography sx={{ marginTop: "auto", paddingTop: "6px", fontSize: TYPE.micro, color: P.inkMuted }}>
          {footer}
        </Typography>
      )}
    </Box>
  );
};

// Long ranges are summed into buckets before being drawn — 92px cannot resolve
// 90 daily points. Summed, not averaged: these are counts.
export const bucket = (values, max = 30) => {
  const nums = (values ?? []).filter((v) => typeof v === "number");
  if (nums.length <= max) return nums;
  const size = Math.ceil(nums.length / max);
  const out = [];
  for (let i = 0; i < nums.length; i += size) out.push(nums.slice(i, i + size).reduce((s, v) => s + v, 0));
  return out;
};

export const stats = (values) => {
  const points = bucket(values, 60);
  return { total: points.reduce((s, v) => s + v, 0), peak: Math.max(...points, 0) };
};

// A · column sparkline. Each column scales from its own baseline.
//
// Twenty buckets, not thirty: the tile gives this 120px, and thirty columns
// with a 2px gutter left each bar about two pixels wide, so a month of mostly
// quiet days read as scattered dots rather than a trend. Twenty bars with a
// 1px gutter are ~5px each and the shape of the month is legible.
//
// A quiet day still draws a short bar in the track colour rather than nothing,
// so the series reads as a continuous baseline with activity above it instead
// of a broken line.
export const BarSparkline = ({ values, delay = 0.45, width = 120, height = 36 }) => {
  const points = bucket(values, 20);
  const peak = Math.max(...points, 0);
  return (
    // Bars cap at 8px and sit right-aligned (latest day last), so a one-day
    // range draws one slim bar rather than a solid block across the tile.
    <Box aria-hidden="true" sx={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end", gap: "1px", width, height }}>
      {points.map((v, i) => (
        <Box
          key={i}
          className="dv-bar"
          style={{ animationDelay: `${delay + i * 0.016}s` }}
          sx={{
            flex: 1,
            minWidth: "2px",
            maxWidth: "8px",
            height: `${v === 0 ? 10 : Math.max(22, (v / (peak || 1)) * 100)}%`,
            borderRadius: "1px",
            background: v === 0 ? P.track : P.accent,
          }}
        />
      ))}
    </Box>
  );
};

export const areaPath = (values, width, height) => {
  const points = bucket(values, 60);
  if (points.length === 0) return null;
  const peak = Math.max(...points, 0);
  const ceiling = peak <= 0 ? 1 : peak * 1.15;
  const base = height - 1;
  // A one-day range has one point: there is no line to draw, so mark the value.
  if (points.length === 1) {
    const y = base - (points[0] / ceiling) * (height - 6);
    return { line: null, area: null, dot: { x: width / 2, y }, ceiling: Math.ceil(ceiling) };
  }
  const line = points
    .map((v, i) => {
      const x = (i / (points.length - 1)) * width;
      const y = base - (v / ceiling) * (height - 6);
      return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  return { line, area: `${line} L${width} ${base} L0 ${base} Z`, ceiling: Math.ceil(ceiling) };
};

// A chart that is already drawn, revealed by a panel sliding off it — the data
// arriving rather than the chart assembling.
export const WipeReveal = ({ delay = 0, children, background = P.surface }) => (
  <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
    {children}
    <Box
      aria-hidden="true"
      className="dv-wipe"
      style={{ animationDelay: `${delay}s` }}
      sx={{ position: "absolute", inset: "-2px", background }}
    />
  </Box>
);

export const AreaChart = ({ values, color, label, height = 96, delay = 0, axis = true, gid }) => {
  const path = areaPath(values, 600, height);
  return (
    <Box sx={{ display: "flex", gap: SPACE.sm, height }}>
      {axis && (
        <Box
          aria-hidden="true"
          sx={{ ...NUM, display: "flex", flexDirection: "column", justifyContent: "space-between", fontSize: "11px", color: P.inkMuted, width: 22, textAlign: "right" }}
        >
          <span>{path?.ceiling ?? 0}</span>
          <span>{Math.round((path?.ceiling ?? 0) / 2)}</span>
          <span>0</span>
        </Box>
      )}
      <WipeReveal delay={delay}>
        {path && (
          <svg viewBox={`0 0 600 ${height}`} preserveAspectRatio="none" role="img" aria-label={label} style={{ width: "100%", height: "100%", display: "block" }}>
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={color} stopOpacity="0.22" />
                <stop offset="1" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            <line x1="0" y1={height / 2} x2="600" y2={height / 2} stroke={P.track} strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <line x1="0" y1={height - 1} x2="600" y2={height - 1} stroke={P.line} strokeWidth="1" vectorEffect="non-scaling-stroke" />
            {path.line && <path d={path.area} fill={`url(#${gid})`} />}
            {path.line && <path d={path.line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />}
            {path.dot && <circle cx={path.dot.x} cy={path.dot.y} r="5" fill={color} vectorEffect="non-scaling-stroke" />}
          </svg>
        )}
      </WipeReveal>
    </Box>
  );
};

// C · heat strip. One cell per day, shaded by sqrt(value/peak) so a quiet day
// is visibly quiet rather than a flat stretch of line.
export const HeatStrip = ({ values, label, delay = 0.5, cellHeight = 26 }) => {
  const points = bucket(values, 30);
  const peak = Math.max(...points, 0);
  return (
    // The strip is at most 48px per day: a month fills any card, while a
    // one-day range draws one modest square instead of a bar across the tile.
    <Box role="img" aria-label={label} sx={{ display: "grid", gridTemplateColumns: `repeat(${points.length || 1}, minmax(0, 1fr))`, gap: "4px", maxWidth: `${(points.length || 1) * 48}px` }}>
      {points.map((v, i) => (
        <Box
          key={i}
          className="dv-pop"
          style={{ animationDelay: `${delay + i * 0.015}s` }}
          title={`${v}`}
          sx={{
            height: cellHeight,
            borderRadius: "4px",
            background: v === 0 ? P.track : P.accent,
            opacity: v === 0 ? 1 : 0.3 + 0.7 * Math.sqrt(v / (peak || 1)),
          }}
        />
      ))}
    </Box>
  );
};

// A card's "nothing here" state: an icon in the same soft chip the KPI tiles
// use, a short title, and a line saying what will appear or why it is empty.
// Card-sized on purpose: ui-components/NoData is a page-level empty state.
export const EmptyState = ({ icon, title, text, children, minHeight = 140 }) => (
  <Box
    role="status"
    sx={{
      flex: 1,
      minHeight,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      gap: SPACE.sm,
      padding: "20px 16px",
      borderRadius: "12px",
      background: P.surface,
      border: `1px dashed ${P.line}`,
    }}
  >
    {icon && (
      <Box aria-hidden="true" sx={{ width: 40, height: 40, borderRadius: "12px", background: P.iconChip, color: P.accent, display: "grid", placeItems: "center", "& svg": { fontSize: 22 } }}>
        {icon}
      </Box>
    )}
    {title && <Typography sx={{ fontSize: TYPE.section, fontWeight: 600, color: P.ink }}>{title}</Typography>}
    {text && <Typography sx={{ maxWidth: 360, fontSize: TYPE.meta, lineHeight: 1.5, color: P.inkMuted }}>{text}</Typography>}
    {children}
  </Box>
);

export const Skeleton = ({ height, radius = "12px" }) => (
  <Box className="dv-skel" sx={{ height, borderRadius: radius, background: P.track }} />
);

export const LoadingView = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: SPACE.lg }} aria-busy="true" aria-live="polite">
    <Box sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
      Loading dashboard figures
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(230px, 100%), 1fr))", gap: SPACE.lg }}>
      {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={132} />)}
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(440px, 100%), 1fr))", gap: SPACE.lg }}>
      <Skeleton height={300} />
      <Skeleton height={300} />
    </Box>
    <Skeleton height={200} />
  </Box>
);
